import type { FunnelPhaseDto } from '@nsb/core';

/** A phase's heading over the funnel: its bar along the days it spans and its name placed over it. */
export interface PhaseHeading {
  key: FunnelPhaseDto['key'];
  name: string;
  phase: FunnelPhaseDto;
  /** Along the funnel's width, in pixels. */
  bar: { from: number; to: number };
  /** Not dated yet: drawn after the last dated bar, its line dashed. */
  dashed: boolean;
  /** Where its name sits: `row` 0 under the bars, a row further down where it would touch a neighbour's. */
  label: { left: number; width: number; row: number };
}

/** A letter of the heading's mono capitals at 13px, and the question mark with its gap. */
const LETTER = 8.6;
const MARK = 26;

/**
 * Every phase's heading over the funnel (Jason, 2026-10-29: "I think we have to show the headings for the phases some
 * how in the funnel"; L-31: "these titles should be where the bars are above the funnel"): each name centred on its
 * bar, running past a bar too narrow for it where it has room, nudged a little, else a row further down, as the marks'
 * labels under the timeline are placed; never past the funnel's edges. The phases come in the process's order, always
 * (L-37: "it changed the order of the phases"): a day between two phases nobody has dated shares the width between the
 * days either side of it evenly (the far edge after the last), a phase whose end is not dated has its line dashed, and
 * a bar never runs back before the one ahead of it.
 */
export function phaseHeadings(phases: { p: FunnelPhaseDto; from: number | null; to: number | null }[], across: number, help: boolean): PhaseHeading[] {
  // The days between the phases, in pixels: the first's start, then each phase's end (else the next one's start).
  const known: (number | null)[] = [phases[0]?.from ?? 0, ...phases.map((ph, i) => ph.to ?? phases[i + 1]?.from ?? null)];
  known[known.length - 1] ??= across;
  const at = [...known];
  for (let i = 1; i < at.length; i++) {
    if (at[i] !== null) continue;
    let j = i;
    while (at[j] === null) j++;
    for (let k = i; k < j; k++) at[k] = at[i - 1]! + ((at[j]! - at[i - 1]!) * (k - i + 1)) / (j - i + 1);
  }
  for (let i = 1; i < at.length; i++) at[i] = Math.max(at[i]!, at[i - 1]!);
  const bars = phases.map((ph, i) => ({ p: ph.p, from: at[i]!, to: at[i + 1]!, dashed: ph.to === null }));
  const taken: { from: number; to: number }[][] = [];
  return bars.map(({ p, from, to, dashed }) => {
    const width = p.name.length * LETTER + (help ? MARK : 0) + 4;
    const inside = (l: number) => Math.min(Math.max(l, 0), Math.max(0, across - width));
    const ideal = (from + to) / 2 - width / 2;
    for (let row = 0; ; row++) {
      const here = (taken[row] ??= []);
      for (const nudge of [0, 10, -10, 20, -20, 28, -28]) {
        const left = inside(ideal + nudge);
        if (here.every((t) => left + width + 8 <= t.from || left >= t.to + 8)) {
          here.push({ from: left, to: left + width });
          return { key: p.key, name: p.name, phase: p, bar: { from, to }, dashed, label: { left, width, row } };
        }
      }
    }
  });
}
